/**
 * 스크롤 깊이 추적 Composable
 * 25%, 50%, 75%, 100% 지점에서 이벤트 발생
 * 수동 초기화/정리 방식으로 라이프사이클 문제 해결
 */

import { ref } from 'vue';
import { useAnalytics } from './useAnalytics';

export function useScrollTracking() {
  const { trackScrollDepth } = useAnalytics();
  const trackedDepths = ref(new Set());
  
  let throttleTimer = null;
  let isInitialized = false;
  
  const calculateScrollDepth = () => {
    if (typeof window === 'undefined') return 0;
    
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    const scrollable = documentHeight - windowHeight;
    if (scrollable <= 0) return 100; // 스크롤할 콘텐츠가 없으면 100%
    
    const scrolled = scrollTop;
    const scrollPercentage = Math.round((scrolled / scrollable) * 100);
    
    return Math.min(scrollPercentage, 100);
  };
  
  const handleScroll = () => {
    if (throttleTimer) return;
    
    throttleTimer = setTimeout(() => {
      try {
        const depth = calculateScrollDepth();
        
        // 25%, 50%, 75%, 100% 지점에서 추적
        const milestones = [25, 50, 75, 100];
        
        milestones.forEach(milestone => {
          if (depth >= milestone && !trackedDepths.value.has(milestone)) {
            trackedDepths.value.add(milestone);
            
            // 스크롤 깊이 추적 (useAnalytics에서 페이지 정보 자동 포함)
            trackScrollDepth(milestone);
          }
        });
      } catch (error) {
        // 스크롤 추적 에러는 조용히 실패 (사용자 경험에 영향 없음)
        if (process.env.NODE_ENV === 'development') {
          console.warn('Scroll tracking error:', error);
        }
      } finally {
        throttleTimer = null;
      }
    }, 250); // 250ms 쓰로틀링
  };
  
  const startTracking = () => {
    if (typeof window === 'undefined' || isInitialized) return;
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    isInitialized = true;
  };
  
  const stopTracking = () => {
    if (typeof window === 'undefined' || !isInitialized) return;
    
    window.removeEventListener('scroll', handleScroll);
    if (throttleTimer) {
      clearTimeout(throttleTimer);
      throttleTimer = null;
    }
    isInitialized = false;
  };
  
  const resetTracking = () => {
    trackedDepths.value.clear();
  };
  
  return {
    startTracking,
    stopTracking,
    resetTracking,
    trackedDepths: trackedDepths.value
  };
}